import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'

import { RootState } from '~/redux/store'

import { BootcampUserEvents, LevelActionType, LevelPageDetailsType } from '~/bootcamp/types/bootcamp.types'
import { checkIfLevelComplete } from '~/bootcamp/pages/BootcampView/BootcampView.utils'

interface BootcampPageSliceType extends LevelPageDetailsType {
  isCompleted: boolean
  levelProgress: BootcampUserEvents | undefined
  isCollapsed: boolean
}

interface BootcampLevelProgressPayload extends BootcampUserEvents {
  level: string
  action: LevelActionType
  isEnglishOnly?: boolean
  isVideoWatched?: boolean
}

const initialState = {
  activities: [],
  chest: undefined,
  content: [],
  help: { encyclopediaPath: '', drillPath: '' },
  isCompleted: false,
  levelProgress: undefined,
  sectionName: '',
  usefulWords: [],
  isCollapsed: false,
  isEnglishOnly: false,
  encyclopediaArticle: null,
  levelIds: [],
} as BootcampPageSliceType

const bootcampPageSlice = createSlice({
  name: 'bootcamp-page',
  initialState,
  reducers: {
    setBootcampPageData: (state, action: PayloadAction<LevelPageDetailsType>) => {
      const {
        activities,
        content,
        help,
        chest,
        sectionName,
        usefulWords,
        isEnglishOnly,
        encyclopediaArticle,
        levelIds,
      } = action.payload
      return {
        ...state,
        activities,
        content,
        help,
        chest,
        sectionName: encyclopediaArticle ? '' : sectionName,
        usefulWords,
        isEnglishOnly,
        encyclopediaArticle,
        levelIds,
      }
    },
    setBootcampIsCollapsed: (state, action) => {
      return {
        ...state,
        isCollapsed: action.payload,
      }
    },

    setBootcampLevelProgress: (state, action: PayloadAction<BootcampLevelProgressPayload | undefined>) => {
      if (action.payload) {
        const { processedLeafs, collectedRewards, level, action: levelAction, isVideoWatched = false } = action.payload
        return {
          ...state,
          levelProgress: { processedLeafs, collectedRewards },
          isCompleted: checkIfLevelComplete(action.payload.processedLeafs, `${level}-${levelAction}`) || isVideoWatched,
        }
      }

      return state
    },
  },
  // The object notation for `createSlice.extraReducers` is deprecated, and will be removed in RTK 2.0. Please use the 'builder callback' notation instead: https://redux-toolkit.js.org/api/createSlice
  extraReducers: (builder) => {
    builder.addCase(HYDRATE, (state, action) => {
      return {
        ...state,
        ...action.payload.bootcampPage,
      }
    })
  },
})

export const selectBootcampPageHelp = (state: RootState): BootcampPageSliceType['help'] => {
  return state.bootcampPage.help
}

export const selectBootcampPageActivities = (state: RootState): BootcampPageSliceType['activities'] => {
  return state.bootcampPage.activities
}

export const selectBootcampPageUsefulWords = (state: RootState): BootcampPageSliceType['usefulWords'] => {
  return state.bootcampPage.usefulWords
}

export const selectBootcampPageChest = (state: RootState): BootcampPageSliceType['chest'] => {
  return state.bootcampPage.chest
}

export const selectBootcampPageLevelProgress = (state: RootState): BootcampPageSliceType['levelProgress'] => {
  return state.bootcampPage.levelProgress
}

export const selectBootcampIsCollapsed = (state: RootState): BootcampPageSliceType['isCollapsed'] => {
  return state.bootcampPage.isCollapsed
}

export const selectBootcampPageCompleted = (state: RootState): BootcampPageSliceType['isCompleted'] => {
  return state.bootcampPage.isCompleted
}

export const selectBootcampPageLevelIds = (state: RootState): BootcampPageSliceType['levelIds'] => {
  return state.bootcampPage.levelIds
}

export const selectBootcampPage = (state: RootState): LevelPageDetailsType => {
  return state.bootcampPage
}

export const selectBootcampPageContent = (state: RootState): BootcampPageSliceType['content'] => {
  return state.bootcampPage.content
}

export const selectBootcampPageEncyclopediaArticle = (
  state: RootState,
): BootcampPageSliceType['encyclopediaArticle'] => {
  return state.bootcampPage.encyclopediaArticle
}

export const selectBootcampPageSectionName = (state: RootState): BootcampPageSliceType['sectionName'] => {
  return state.bootcampPage.sectionName
}

export const selectBootcampPageIsEnglishOnly = (state: RootState): BootcampPageSliceType['isEnglishOnly'] => {
  return state.bootcampPage.isEnglishOnly
}

export const { setBootcampPageData, setBootcampLevelProgress, setBootcampIsCollapsed } = bootcampPageSlice.actions
export default bootcampPageSlice.reducer
